import { FC, useCallback } from 'react';

import * as zod from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  createClientMutationId,
  useSetUserErrorCodes,
  useUserErrorCode,
} from 'shared-lib';
import {
  allListObject,
  ActionContainer,
  ErrorField,
  FormErrorCode,
  FormErrorCodes,
  FormInputRow,
  InputField,
  SelectMenuInput,
  SwitchInput,
} from 'shared-ui';

import { elevateAccountTypesList } from '../../../authn';
import {
  IdentityUserCreateWithoutPasswordInput,
  useIdentityUserCreate,
} from '../mutations';
import { IdentityUserAccountsErrorCodes } from '../lib';

const formSchema = zod.object({
  accountType: zod.string().min(1, { message: 'AccountType is required' }),
  email: zod.string().min(1, { message: 'Email is required' }).email({
    message: 'Must be a valid email',
  }),
  sendPasswordResetNotification: zod.boolean(),
});

type FormSchema = zod.infer<typeof formSchema>;

export type CreatedIdentityUser = {
  readonly __id: string;
  readonly accountTypes: ReadonlyArray<string | null | undefined>;
  readonly code: number;
  readonly createdAt: string;
  readonly email: string;
  readonly firstName: string | null | undefined;
  readonly id: string;
  readonly identityUserId: string;
  readonly lastLoggedIn: string | null | undefined;
  readonly lastName: string | null | undefined;
  readonly updatedAt: string;
};

type Props = {
  onCreate: (identityUser: CreatedIdentityUser) => void;
  onCancel: () => void;
};
export const IdentityUserCreateContainer: FC<Props> = ({ onCreate }) => {
  const defaultValues = {
    accountType: '',
    email: '',
    enableTwoFactorAuthentication: true,
    isTestUser: true,
  };

  const setErrorCode = useSetUserErrorCodes();

  const { register, handleSubmit, getValues, formState, control } =
    useForm<FormSchema>({
      resolver: zodResolver(formSchema),
      defaultValues,
      mode: 'onChange',
    });

  const userErrorCode = useUserErrorCode();
  const [createIdentityUser, isCreatingIdentityUser] = useIdentityUserCreate();

  const onSave = useCallback(
    async (form: FormSchema) => {
      setErrorCode(null);

      if (!form.accountType || !form.email) {
        return;
      }

      const input: IdentityUserCreateWithoutPasswordInput = {
        clientMutationId: createClientMutationId(),
        email: form.email!,
        accountType: form.accountType!,
        sendPasswordResetNotification: form.sendPasswordResetNotification,
      };

      const response = await createIdentityUser({ input });

      if (response.data.userErrors && response.data.userErrors.length > 0) {
        setErrorCode(response.data.userErrors);
      }

      if (response.data.payload?.node) {
        onCreate(response.data.payload.node);
      }
    },
    [createIdentityUser],
  );

  const onSubmit = handleSubmit(
    async form => {
      await onSave(form);
    },
    invalid => {
      console.error(invalid);
    },
  );

  const { errors, isValid, isDirty } = formState;

  let errorDisplay = <FormErrorCodes />;

  if (
    userErrorCode &&
    userErrorCode === IdentityUserAccountsErrorCodes.USER_EMAIL_IS_IN_USE
  ) {
    errorDisplay = <FormErrorCode message='Email is already in use' />;
  }

  return (
    <div className='m-4 flex flex-col items-center justify-center'>
      {errorDisplay}
      <form onSubmit={onSubmit}>
        <ActionContainer
          isDirty={isDirty}
          showSubmit={true}
          showCancel={true}
          isSaving={isCreatingIdentityUser}
          isDisabled={!isValid}
          submitText='Create Identity'
          titleTop={`Create Identity`}
        >
          <FormInputRow name='accountType' title='Account Type *'>
            <SelectMenuInput
              name='accountType'
              items={elevateAccountTypesList}
              control={control}
              disabled={false}
              selectedItem={
                elevateAccountTypesList.filter(
                  x => x.id == getValues('accountType'),
                )[0] || allListObject
              }
            />
            <ErrorField field={errors.accountType} />
          </FormInputRow>

          <FormInputRow name='email' title='Email *'>
            <InputField
              register={register}
              name='email'
              required={true}
              placeholder='Identity User Email'
            />
            <ErrorField field={errors.email} />
          </FormInputRow>

          <FormInputRow
            name='sendPasswordResetNotification'
            title='Send Notification Email *'
          >
            <SwitchInput
              control={control}
              name='sendPasswordResetNotification'
              defaultChecked={false}
            />
            <ErrorField field={errors.sendPasswordResetNotification} />
          </FormInputRow>
        </ActionContainer>
      </form>
    </div>
  );
};
